import {
  ComponentProps,
  cloneElement,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  autoPlacement,
  offset as offsetMiddleware,
  type Placement,
  shift,
} from "@floating-ui/dom";
import {
  faDownload,
  faExclamationCircle,
  faFile,
  faSpinner,
} from "@fortawesome/pro-regular-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { ColumnDef, Row } from "@tanstack/react-table";
import papaparse from "papaparse";

import { GenericObject } from "src/api/flowTypes";
import {
  FloatingWindow,
  useEnclosingFloatingWindowId,
} from "src/base-components/FloatingWindow/FloatingWindow";
import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { type ComputePositionOptions } from "src/base-components/FloatingWindow/positioning";
import { TableComp } from "src/base-components/Table";
import { FileLocator } from "src/clients/case-management";
import { FileAttributes } from "src/clients/files";
import { ObjectDetailPane } from "src/dataTable/ObjectDetailPane";
import { Button } from "src/design-system/Button";
import { Icon } from "src/design-system/Icon";
import { Pill } from "src/design-system/Pill";
import { toastActions } from "src/design-system/Toast/utils";
import { useFileViewerWindowMode } from "src/files/fileViewerWindowMode";
import { ErrorBoundary } from "src/utils/ErrorBoundary";
import { downloadFileFromUrl, humanReadableSize } from "src/utils/files";
import { HeadlessUIPlacement } from "src/utils/headlessui";
import { assertUnreachable, LooseLiteral } from "src/utils/typeUtils";

const CONTENT_TYPE_GROUPS = {
  "application/pdf": "pdf",
  "image/jpeg": "image",
  "image/png": "image",
  "image/webp": "image",
  "video/mp4": "video",
  "text/csv": "csv",
  "text/plain": "text",
  "text/html": "html",
  "application/json": "json",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
} as const;

type KnownContentType = keyof typeof CONTENT_TYPE_GROUPS;

// Loosely typed content type, allowing for unknown content types
type ContentType = LooseLiteral<KnownContentType>;

export type FileGroup =
  | (typeof CONTENT_TYPE_GROUPS)[KnownContentType]
  | "unsupported";

export const getFileGroup = (contentType: ContentType): FileGroup =>
  CONTENT_TYPE_GROUPS[contentType as KnownContentType] ?? "unsupported";

type FileViewerProps = {
  // File Viewer accepts either FileAttributes or File or undefined:
  // FileAttributes is a Files API response
  // File is a locally uploaded file
  // undefined is a placeholder file
  file: FileAttributes | File | undefined;
  children: React.ReactElement<{ onClick?: () => void }>;
  placement?: ComponentProps<typeof FloatingWindow>["placement"];
  offset?: ComponentProps<typeof FloatingWindow>["offset"];
};

export const FileDownloadButton: React.FC<{ file: FileAttributes | File }> = ({
  file,
}) => {
  const href = useFileUrl(file);

  if (!href) {
    return null;
  }

  return (
    <a
      aria-label={`Download ${file.name}`}
      className="cursor-pointer text-gray-500 hover:text-gray-800"
      download={file.name}
      href={href}
      onClick={(e) => e.stopPropagation()}
    >
      <Icon icon={faDownload} size="xs" />
    </a>
  );
};

export const getContentType = (file: FileAttributes | File): ContentType => {
  if (file instanceof File) {
    return file.type;
  }

  return file.content_type;
};

const useFileUrl = (file: FileAttributes | File): string | null => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      setObjectUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setObjectUrl(file.download_url ?? null);
    }
  }, [file]);

  return objectUrl;
};

export const FileViewerContent: React.FC<{
  file: FileAttributes | File;
  locator?: FileLocator | null;
}> = ({ file, locator }) => {
  const group = getFileGroup(getContentType(file));

  const renderViewer = (): React.ReactNode => {
    switch (group) {
      case "image":
        return <ImageViewer file={file} />;
      case "video":
        return <VideoViewer file={file} />;
      case "pdf":
        return <PDFViewer file={file} locator={locator} />;
      case "csv":
        return <CSVViewer file={file} />;
      case "text":
        return <TextViewer file={file} />;
      case "html":
        return <HTMLViewer file={file} />;
      case "json":
        return <JSONViewer file={file} />;
      case "xlsx":
        return <XLSXViewer file={file} />;
      case "docx":
        return <DOCXViewer file={file} />;
      case "unsupported":
        return <UnsupportedFileViewer file={file} />;
      default:
        assertUnreachable(group);
    }
  };

  return (
    <ErrorBoundary fallbackErrorContent={<FallbackViewer state="error" />}>
      {renderViewer()}
    </ErrorBoundary>
  );
};

/** Stand-in metadata for a file whose record does not exist yet. */
export const PLACEHOLDER_FILE = {
  name: "Placeholder file",
  size: 0,
  content_type: "text/plain",
  download_url: null,
} as FileAttributes;

/** Name and size, as they read in a file viewer window's header. */
export const FileViewerTitle: React.FC<{ file: FileAttributes | File }> = ({
  file,
}) => (
  <div className="flex items-center gap-1" data-loc="file-viewer-title">
    <span className="pr-2 font-inter-normal-12px text-gray-800">
      {file.name}
    </span>
    <Pill size="sm" variant="gray">
      {humanReadableSize(file.size)}
    </Pill>
  </div>
);

/** Window body for a file that may not exist yet: `undefined` is a placeholder. */
export const FileViewerBody: React.FC<{
  file: FileAttributes | File | undefined;
}> = ({ file }) =>
  file === undefined ? (
    <FallbackViewer state="placeholder" />
  ) : (
    <FileViewerContent file={file} />
  );

/**
 * Identifies the window by the file it shows, so a trigger that is re-created
 * while the viewer is open reopens the same window instead of stacking a
 * second one on top of it.
 */
const fileViewerWindowId = (file: FileAttributes | File | undefined): string =>
  file === undefined
    ? "file-viewer-placeholder"
    : `file-viewer-${file.name}-${file.size}`;

/**
 * Restates `FloatingWindow`'s own positioning — placement, offset and shift —
 * as options the floating-window store can compute a position from, so both
 * viewer modes open in the same place relative to their trigger.
 */
const toComputePositionOptions = (
  placement: HeadlessUIPlacement | "auto" = "bottom start",
  [crossAxis, mainAxis]: [number, number] = [0, 4],
): ComputePositionOptions => ({
  placement:
    placement === "auto"
      ? undefined
      : (placement.replace(" ", "-") as Placement),
  middleware: [
    offsetMiddleware({ crossAxis, mainAxis }),
    shift({ padding: 8 }),
    ...(placement === "auto" ? [autoPlacement()] : []),
  ],
});

/** The viewer as a window of its own, unmounted together with its trigger. */
const InlineFileViewer: React.FC<FileViewerProps> = ({
  file,
  children,
  placement,
  offset,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const triggerWithClick = children
    ? cloneElement(children, {
        onClick: () => setIsOpen(true),
      })
    : null;

  const displayedFile = file ?? PLACEHOLDER_FILE;
  const isFilePDF = getFileGroup(getContentType(displayedFile)) === "pdf";

  return (
    <FloatingWindow
      button={triggerWithClick}
      height="h-144"
      isOpen={isOpen}
      minHeight="h-64"
      minWidth="w-96"
      pinnable={false}
      title={<FileViewerTitle file={displayedFile} />}
      titleRight={
        isFilePDF ? null : <FileDownloadButton file={displayedFile} />
      }
      width="w-176"
      draggable
      maximizable
      resizeable
      onClose={() => setIsOpen(false)}
      placement={placement}
      offset={offset}
    >
      <FileViewerBody file={file} />
    </FloatingWindow>
  );
};

/** The viewer as a store-owned window that outlives its trigger. */
const SharedFileViewer: React.FC<FileViewerProps> = ({
  file,
  children,
  placement,
  offset,
}) => {
  const { open } = useFloatingWindowsActions();
  const triggerRef = useRef<HTMLSpanElement>(null);
  // The viewer is large enough to overlap the window it opens from, which
  // raises itself on every click inside it. Naming that opener keeps the
  // viewer above it instead of behind it.
  const openedFrom = useEnclosingFloatingWindowId();

  const handleOpen = (): void => {
    open(
      {
        id: fileViewerWindowId(file),
        type: "file-viewer",
        target: triggerRef,
        positionOptions: toComputePositionOptions(placement, offset),
        openedFrom,
      },
      { file },
    );
  };

  return (
    <div className="h-full">
      <span ref={triggerRef}>
        {children ? cloneElement(children, { onClick: handleOpen }) : null}
      </span>
    </div>
  );
};

const FileViewer: React.FC<FileViewerProps> = (props) =>
  useFileViewerWindowMode() === "shared" ? (
    <SharedFileViewer {...props} />
  ) : (
    <InlineFileViewer {...props} />
  );

const HTMLViewer: React.FC<{ file: FileAttributes | File }> = ({ file }) => {
  const [hasError, setHasError] = useState(false);
  const [html, setHtml] = useState("");
  const fileUrl = useFileUrl(file);

  useEffect(() => {
    // Reset state when fileUrl changes
    setHasError(false);
    setHtml("");

    const ac = new AbortController();

    if (!fileUrl) {
      return;
    }

    const fetchHtml = async () => {
      try {
        const response = await fetch(fileUrl, { signal: ac.signal });

        if (!response.ok) {
          throw new Error("Failed to fetch HTML file");
        }

        const html = await response.text();

        setHtml(html);
      } catch {
        if (!ac.signal.aborted) {
          setHasError(true);
        }
      }
    };

    fetchHtml();

    return () => {
      ac.abort();
    };
  }, [fileUrl]);

  if (!fileUrl) {
    return <FallbackViewer message="HTML file not found" state="error" />;
  }

  if (hasError) {
    return (
      <FallbackViewer message="HTML file could not be loaded" state="error" />
    );
  }

  return (
    <div className="decideScrollbar h-full w-full overflow-auto p-4">
      <div className="flex h-full min-h-[1px] min-w-full items-center justify-center">
        <iframe
          className="h-full w-full border-none object-contain"
          data-loc="html-viewer"
          sandbox=""
          srcDoc={html}
          title={file.name}
          onError={() => setHasError(true)}
        />
      </div>
    </div>
  );
};

const ImageViewer: React.FC<{ file: FileAttributes | File }> = ({ file }) => {
  const [hasError, setHasError] = useState(false);
  const fileUrl = useFileUrl(file);

  if (!fileUrl) {
    return <FallbackViewer message="Image not found" state="error" />;
  }

  if (hasError) {
    return <FallbackViewer message="Image could not be loaded" state="error" />;
  }

  return (
    <div className="decideScrollbar h-full w-full overflow-auto p-4">
      <div className="flex min-h-full min-w-full items-center justify-center">
        <img
          alt={file.name}
          className="object-contain"
          src={fileUrl}
          style={{ maxWidth: "100%", maxHeight: "100%" }}
          onError={() => setHasError(true)}
        />
      </div>
    </div>
  );
};

const useFileBlobUrl = (
  file: FileAttributes | File,
): { objectUrl: string | null; isLoading: boolean; isError: boolean } => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const isLocalFile = file instanceof File;
  const fileUrl = useFileUrl(file);

  useEffect(() => {
    setIsLoading(true);
    setIsError(false);

    if (!fileUrl) {
      setIsError(true);
      setIsLoading(false);
      return;
    }

    // A local File is already served from an object URL created by useFileUrl
    if (isLocalFile) {
      setObjectUrl(fileUrl);
      setIsLoading(false);
      return;
    }

    let ignore = false;
    fetch(fileUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        if (!ignore) {
          setObjectUrl(URL.createObjectURL(blob));
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!ignore) {
          setIsError(true);
          setIsLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [fileUrl, isLocalFile]);

  useEffect(() => {
    return () => {
      if (objectUrl && !fileUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl, fileUrl]);

  return { objectUrl, isLoading, isError };
};

const VideoViewer: React.FC<{ file: FileAttributes | File }> = ({ file }) => {
  const { objectUrl, isLoading, isError } = useFileBlobUrl(file);
  // Tracks which source failed to play, so the error clears once a new
  // object URL arrives
  const [erroredUrl, setErroredUrl] = useState<string | null>(null);
  const hasPlaybackError = objectUrl !== null && erroredUrl === objectUrl;

  if (isError || hasPlaybackError) {
    return <FallbackViewer message="Video could not be loaded" state="error" />;
  }

  if (isLoading || !objectUrl) {
    return <FallbackViewer state="loading" />;
  }

  return (
    <div className="decideScrollbar h-full w-full overflow-auto p-4">
      <div className="flex min-h-full min-w-full items-center justify-center">
        <video
          aria-label={file.name}
          className="object-contain"
          controls
          data-loc="video-viewer"
          src={objectUrl}
          style={{ maxWidth: "100%", maxHeight: "100%" }}
          onError={() => setErroredUrl(objectUrl)}
        />
      </div>
    </div>
  );
};

const pdfFragment = (locator?: FileLocator | null): string =>
  locator?.page ? `page=${locator.page}&view=FitH` : "view=FitH";

const PDFViewer: React.FC<{
  file: FileAttributes | File;
  locator?: FileLocator | null;
}> = ({ file, locator }) => {
  const { objectUrl, isLoading, isError } = useFileBlobUrl(file);

  if (isLoading) {
    return <FallbackViewer state="loading" />;
  }

  if (isError || !objectUrl) {
    return <FallbackViewer state="error" />;
  }

  const src = `${objectUrl}#${pdfFragment(locator)}`;

  return (
    <iframe
      className="h-full w-full border-0"
      data-loc="pdf-viewer"
      // Browsers do not re-navigate a frame on a hash-only src change, so the
      // key remounts the iframe whenever the locator moves.
      key={src}
      src={src}
      title={file.name ?? ""}
    />
  );
};

const CSVViewer: React.FC<{ file: FileAttributes | File }> = ({ file }) => {
  const [data, setData] = useState<Record<string, string>[]>([
    {
      x: "1",
      y: "2",
      z: "3",
      w: "4",
    },
  ]);
  const [columns, setColumns] = useState<ColumnDef<Record<string, string>>[]>([
    {
      accessorKey: "x",
      header: "",
    },
    {
      accessorKey: "y",
      header: "",
    },
    {
      accessorKey: "z",
      header: "",
    },
    {
      accessorKey: "w",
      header: "",
    },
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const downloadUrl = useFileUrl(file);

  useEffect(() => {
    setLoading(true);
    setError(null);

    if (!downloadUrl) {
      setError("File download url is not available");
      setLoading(false);
      return;
    }

    const defineColumn = (
      header: string,
    ): ColumnDef<Record<string, string>> => ({
      accessorKey: header,
      header: () => (
        <div className="h-full border-gray-200 border-t border-l p-1 font-inter-semibold-12px">
          {header}
        </div>
      ),
      cell: ({ row }) => (
        <div className="h-full truncate border-gray-200 border-l p-1 font-inter-normal-12px">
          {row.getValue(header)}
        </div>
      ),
      size: 150,
      minSize: 100,
      maxSize: 300,
    });

    // Intentionally using the default export to mock the parse function
    // eslint-disable-next-line import-x/no-named-as-default-member
    papaparse.parse(downloadUrl, {
      download: true,
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      transform: (value) => value.trim(),
      complete: (results) => {
        if (results.errors.length > 0) {
          setError(results.errors[0].message);
          return;
        }

        const headers = results.meta.fields || [];
        setColumns(headers.map(defineColumn));
        setData(results.data as Record<string, string>[]);
        setLoading(false);
      },
      error: (error) => {
        setError(error.message);
        setLoading(false);
      },
    });
  }, [downloadUrl]);

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-50">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-white pt-2" data-loc="csv-viewer">
      <TableComp
        columns={columns}
        data={data}
        headerPropsGetter={() => ({
          classNameOverrides: "bg-gray-50",
        })}
        isLoading={loading}
        loadingRowsCount={9}
        rowClassName="hover:bg-gray-50"
        variant="default"
      />
    </div>
  );
};

const TextViewer: React.FC<{ file: FileAttributes | File }> = ({ file }) => {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const downloadUrl = useFileUrl(file);

  useEffect(() => {
    setLoading(true);
    setIsError(false);

    if (!downloadUrl) {
      setIsError(true);
      setLoading(false);
      return;
    }

    fetch(downloadUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Could not fetch file");
        }
        return response.text();
      })
      .then((text) => setText(text))
      .catch(() => setIsError(true))
      .finally(() => setLoading(false));
  }, [downloadUrl]);

  if (loading) {
    return <FallbackViewer state="loading" />;
  }

  if (isError) {
    return <FallbackViewer state="error" />;
  }

  return (
    <div className="h-full w-full bg-white" data-loc="text-viewer">
      <pre className="h-full overflow-auto whitespace-pre-wrap break-words text-sm">
        {text}
      </pre>
    </div>
  );
};

const JSONViewer: React.FC<{ file: FileAttributes | File }> = ({ file }) => {
  const [json, setJson] = useState<GenericObject>({});
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const downloadUrl = useFileUrl(file);

  useEffect(() => {
    setLoading(true);
    setIsError(false);

    if (!downloadUrl) {
      setIsError(true);
      setLoading(false);
      return;
    }

    fetch(downloadUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Could not fetch file");
        }
        return response.json();
      })
      .then((json) => setJson(json))
      .catch(() => setIsError(true))
      .finally(() => setLoading(false));
  }, [downloadUrl]);

  if (loading) {
    return <FallbackViewer state="loading" />;
  }

  if (isError) {
    return <FallbackViewer state="error" />;
  }

  return (
    <ObjectDetailPane
      dataLoc="json-file-viewer"
      defaultInspectDepth={3}
      innerDimensionClass="h-full"
      jsonObject={json}
      enableClipboard
    />
  );
};

const XLSXViewer: React.FC<{ file: FileAttributes | File }> = ({ file }) => {
  const downloadUrl = useFileUrl(file);
  const xlsxQuery = useQuery({
    queryKey: ["xlsx", downloadUrl],
    queryFn: async () => {
      if (!downloadUrl) {
        throw new Error("File download url is not available");
      }

      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error("Failed to fetch file");

      const arrayBuffer = await response.arrayBuffer();
      const { read: xlsxRead, utils: xlsxUtils } = await import("xlsx");
      const workbook = xlsxRead(arrayBuffer, { type: "array" });

      const sheetsData = workbook.SheetNames.map((sheetName: string) => ({
        name: sheetName,
        data: xlsxUtils.sheet_to_json(workbook.Sheets[sheetName], {
          header: 1,
          defval: "",
          blankrows: false,
        }),
      }));

      const activeSheet = sheetsData[0];
      const rows = activeSheet.data as unknown[][];

      if (rows.length === 0) {
        throw new Error("No data found in spreadsheet");
      }

      const columns = rows[0].map((_, index) => ({
        accessorKey: index.toString(),
        header: () => (
          <div className="h-full border-gray-200 border-t border-l p-1 font-inter-semibold-12px">
            {rows[0][index]?.toString() || `Column ${index + 1}`}
          </div>
        ),
        cell: ({ row }: { row: Row<Record<string, string>> }) => (
          <div className="h-full truncate border-gray-200 border-l p-1 font-inter-normal-12px">
            {row.getValue(index.toString())}
          </div>
        ),
        size: 150,
        minSize: 100,
        maxSize: 300,
      }));

      const tableData = rows
        .slice(1)
        .map((row) =>
          Object.fromEntries(
            row.map((cell, index) => [
              index.toString(),
              cell?.toString() ?? "",
            ]),
          ),
        );

      return {
        columns,
        data: tableData,
        sheetNames: workbook.SheetNames,
        activeSheetName: activeSheet.name,
      };
    },
  });

  if (xlsxQuery.error) {
    return (
      <FallbackViewer
        message={(xlsxQuery.error as Error).message}
        state="error"
      />
    );
  }

  return (
    <div className="h-full w-full bg-white pt-2" data-loc="xlsx-viewer">
      <TableComp
        columns={xlsxQuery.data?.columns ?? []}
        data={xlsxQuery.data?.data ?? []}
        headerPropsGetter={() => ({
          classNameOverrides: "bg-gray-50",
        })}
        isLoading={xlsxQuery.isPending}
        loadingRowsCount={9}
        rowClassName="hover:bg-gray-50"
        variant="default"
      />
    </div>
  );
};

const DOCXViewer: React.FC<{ file: FileAttributes | File }> = ({ file }) => {
  const downloadUrl = useFileUrl(file);
  const docxQuery = useQuery({
    queryKey: ["docx", downloadUrl],
    queryFn: async () => {
      if (!downloadUrl) {
        throw new Error("File download url is not available");
      }

      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error("Failed to fetch file");

      const arrayBuffer = await response.arrayBuffer();
      const { default: mammoth } = await import("mammoth");
      const result = await mammoth.convertToHtml({ arrayBuffer });

      return result.value;
    },
  });

  if (docxQuery.error) {
    return (
      <FallbackViewer
        message={(docxQuery.error as Error).message}
        state="error"
      />
    );
  }

  if (docxQuery.isPending) {
    return <FallbackViewer state="loading" />;
  }

  return (
    <div className="decideScrollbar h-full w-full overflow-auto bg-white p-4">
      <iframe
        className="h-full w-full border-none"
        data-loc="docx-viewer"
        sandbox=""
        srcDoc={docxQuery.data}
        title={file.name}
      />
    </div>
  );
};

const UnsupportedFileViewer: React.FC<{ file: FileAttributes | File }> = ({
  file,
}) => {
  const href = useFileUrl(file);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (!href) {
      return;
    }

    setIsDownloading(true);
    try {
      await downloadFileFromUrl(href, file.name);
    } catch {
      toastActions.failure({ title: `Downloading ${file.name} failed` });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <FallbackViewer
      message="File type isn't supported for viewing. Download to view it."
      state="error"
      action={
        href && (
          <Button
            dataLoc="unsupported-file-download"
            iconLeft={faDownload}
            isLoading={isDownloading}
            size="sm"
            variant="secondary"
            onClick={handleDownload}
          >
            Download
          </Button>
        )
      }
    />
  );
};

const FallbackViewer: React.FC<{
  message?: string;
  state: "error" | "loading" | "placeholder";
  action?: React.ReactNode;
}> = ({ message, state, action }) => {
  const getBackgroundColor = () => {
    switch (state) {
      case "error":
        return "bg-red-100";
      case "loading":
        return "bg-indigo-100";
      case "placeholder":
        return "bg-gray-100";
    }
  };

  return (
    <div className="flex h-full w-full flex-col items-center justify-center bg-gray-50">
      <div
        className={`mb-2 flex h-12 w-12 items-center justify-center rounded-lg ${getBackgroundColor()}`}
      >
        <div className="text-4xl text-gray-400">
          {state === "error" && (
            <Icon size="md" variant="error" icon={faExclamationCircle} />
          )}
          {state === "loading" && <Icon icon={faSpinner} spin />}
          {state === "placeholder" && <Icon icon={faFile} />}
        </div>
      </div>
      {state === "error" && (
        <>
          <span className="mb-1 font-inter-semibold-13px text-gray-800">
            File cannot be viewed
          </span>
          <span className="font-inter-normal-12px text-gray-500">
            {message}
          </span>
        </>
      )}
      {state === "loading" && (
        <span className="mb-1 font-inter-semibold-13px text-gray-800">
          Loading file...
        </span>
      )}
      {state === "placeholder" && (
        <>
          <span className="mb-1 font-inter-semibold-13px text-gray-800">
            Placeholder file
          </span>
          <span className="font-inter-normal-12px text-gray-500">
            Your file will be visible here
          </span>
        </>
      )}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
};

export { FileViewer };
